import { WorkOrderStatus, WorkOrderType, type TenantCampaignWithCapabilitiesDto, type WorkOrderDto, type WorkOrderListItemDto } from '@signalvote/shared';
import { useCommitteeFacts, useWorkOrder, useWorkOrders } from '@/hooks/queries';
import type { ExpressFlowContext } from './expressProgress';

/** How many Work Orders the Express steps read to find the Draft; the list is newest first. */
export const EXPRESS_WORK_ORDER_PAGE = { pageSize: 50 } as const;

/** The newest Draft Text send in a newest-first list: the one the Express steps fill in. */
export function newestDraftTextSend(items: readonly WorkOrderListItemDto[]): WorkOrderListItemDto | undefined {
  return items.find((item) => item.type === WorkOrderType.TextSend && item.status === WorkOrderStatus.Draft);
}

/**
 * The Text send the Express steps work on: the newest one not yet paid. Once Pay raises its invoice it
 * is Invoiced, and stays here until the card goes through.
 */
function newestUnpaidTextSend(items: readonly WorkOrderListItemDto[]): WorkOrderListItemDto | undefined {
  return items.find(
    (item) =>
      item.type === WorkOrderType.TextSend &&
      (item.status === WorkOrderStatus.Draft || item.status === WorkOrderStatus.Invoiced),
  );
}

export interface ExpressTextSend {
  /** The campaign's newest unpaid Text send: the Draft the Express steps fill in, or the Invoiced one being paid. */
  workOrder: WorkOrderDto | null;
  isLoading: boolean;
  isError: boolean;
}

/** The Text send the Express steps work on, read from the selected campaign's Work Orders. */
export function useExpressTextSend(): ExpressTextSend {
  const list = useWorkOrders(EXPRESS_WORK_ORDER_PAGE);
  const draftId = newestUnpaidTextSend(list.data?.items ?? [])?.id;
  const detail = useWorkOrder(draftId);
  return {
    workOrder: detail.data ?? null,
    isLoading: list.isLoading || (draftId !== undefined && detail.isLoading),
    isError: list.isError || detail.isError,
  };
}

/** Everything /express/send decides its steps from; null while it loads. */
export function useExpressFlow(campaign: TenantCampaignWithCapabilitiesDto): {
  context: ExpressFlowContext | null;
  isError: boolean;
} {
  const send = useExpressTextSend();
  const committee = useCommitteeFacts();
  const isError = send.isError || committee.isError;
  if (isError || send.isLoading || !committee.data) return { context: null, isError };
  return { context: { campaign, workOrder: send.workOrder, committee: committee.data }, isError };
}
